import { ProposalSummaryDisplayMode, ProposalTotalsDisplayMode } from '@/lib/types'
import type { DetailChange, QuoteDetailData } from '@/lib/api/quote-details'
import { isPackageHeader } from '@/lib/utils'
import { Decimal } from 'decimal.js'

type InstalledPriceFields = {
  dsy_proposaldisplay_showroompriceasinstalled?: boolean | null
  dsy_proposaldisplay_showsystempriceasinstalled?: boolean | null
}

type PriceSummary = {
  product: { price: number }
  subtotal: number
}

type CategorySummary = {
  product: { price: number }
  labor: { price: number }
  supplies: { price: number }
}

type SystemChange = {
  newQuoteDetail?: { dsy_cpqfields: { dsy_system: number | null } }
  previousQuoteDetail?: { dsy_cpqfields: { dsy_system: number | null } }
}

type SystemDetail = {
  dsy_cpqfields: { dsy_system: number | null }
}

export function isPriceIncludingLabor(fields: InstalledPriceFields): boolean {
  return Boolean(
    fields.dsy_proposaldisplay_showroompriceasinstalled ||
      fields.dsy_proposaldisplay_showsystempriceasinstalled
  )
}

/**
 * How a labor-type line reads on the proposal: time and material shows its
 * hours at the hourly rate; fixed bid hides it unless prices include labor,
 * where it reads like any product row.
 */
export function getLaborRowDisplay({
  isTimeAndMaterial,
  priceIncludingLabor,
}: {
  isTimeAndMaterial: boolean
  priceIncludingLabor: boolean
}): 'hours' | 'hidden' | 'product' {
  if (isTimeAndMaterial) return 'hours'
  return priceIncludingLabor ? 'product' : 'hidden'
}

export function normalizeProposalSummaryDisplayMode(
  value: number | null | undefined
): ProposalSummaryDisplayMode {
  if (value === ProposalSummaryDisplayMode.None) return ProposalSummaryDisplayMode.None
  if (value === ProposalSummaryDisplayMode.SystemsIncludingLabor) {
    return ProposalSummaryDisplayMode.SystemsIncludingLabor
  }
  return ProposalSummaryDisplayMode.ProductsAndLabor
}

export function normalizeProposalTotalsDisplayMode(
  value: number | null | undefined
): ProposalTotalsDisplayMode {
  return value === ProposalTotalsDisplayMode.ProductsAndLabor
    ? ProposalTotalsDisplayMode.ProductsAndLabor
    : ProposalTotalsDisplayMode.Subtotal
}

export function getProductDisplayPrice(summary: PriceSummary, priceIncludingLabor: boolean): number {
  return priceIncludingLabor ? summary.subtotal : summary.product.price
}

/**
 * Whether the Unit price / Total columns render at all on a customer-facing
 * table — the proposal and the change order read the same rule.
 *
 * Item and package pricing are independent switches, so the columns survive
 * per-item prices being switched off as long as package pricing is on AND this
 * section actually holds a package. Without the last term every cell under them
 * is null and the column header reads as a bug.
 */
export function showsPriceColumns({
  showItemPrice,
  showPackagePrice,
  hasPackageHeader,
}: {
  showItemPrice: boolean
  showPackagePrice: boolean
  hasPackageHeader: boolean
}): boolean {
  return showItemPrice || (showPackagePrice && hasPackageHeader)
}

export function getSystemIds(details: SystemDetail[]): (number | null)[] {
  return Array.from(new Set(details.map((detail) => detail.dsy_cpqfields.dsy_system)))
}

export function getChangedSystemIds(changes: SystemChange[]): (number | null)[] {
  const details = changes.flatMap((change) =>
    [change.newQuoteDetail, change.previousQuoteDetail].filter((detail): detail is SystemDetail => detail != null)
  )

  return getSystemIds(details)
}

export function includeUnassignedSystem(
  systemIds: number[],
  hasUnassignedItems: boolean
): (number | null)[] {
  return hasUnassignedItems ? [...systemIds, null] : systemIds
}

export function getSummaryCategoryAmounts(summary: CategorySummary): { products: number; labor: number } {
  return {
    products: new Decimal(summary.product.price).plus(summary.supplies.price).toNumber(),
    labor: summary.labor.price,
  }
}

export function getStoredSubtotalAdjustment(authoritativeSubtotal: number, calculatedSubtotal: number): number {
  return new Decimal(authoritativeSubtotal).sub(calculatedSubtotal).toDecimalPlaces(2).toNumber()
}

export function sortProposalDetailChanges(
  changes: DetailChange[],
  sortDetails: (details: QuoteDetailData[]) => QuoteDetailData[]
): DetailChange[] {
  const effectiveDetails = changes.flatMap((change) => {
    const detail = change.newQuoteDetail ?? change.previousQuoteDetail
    return detail ? [detail] : []
  })
  const changedGlobalIds = new Set(
    effectiveDetails.map((detail) => detail.dsy_cpqfields.dsy_globalid).filter(Boolean)
  )
  const changeByDetailId = new Map(
    changes.flatMap((change) => {
      const detail = change.newQuoteDetail ?? change.previousQuoteDetail
      return detail ? [[detail.quotedetailid, change] as const] : []
    })
  )
  const normalizedDetails = effectiveDetails.map((detail) => {
    const parentGlobalId = detail.dsy_cpqfields.dsy_parentglobalid
    if (!parentGlobalId || changedGlobalIds.has(parentGlobalId)) return detail

    return {
      ...detail,
      dsy_cpqfields: {
        ...detail.dsy_cpqfields,
        dsy_parentglobalid: null,
      },
    }
  })

  return sortDetails(normalizedDetails).flatMap((detail) => {
    const change = changeByDetailId.get(detail.quotedetailid)
    return change ? [change] : []
  })
}

/**
 * Header global id of every row inside a package: its members and their
 * accessories. A key pointing at no package header in this snapshot is no
 * package, exactly as the proposal page reads it.
 */
function getPackageHeaderByGlobalId(details: QuoteDetailData[]): Map<string, string> {
  const headerIds = new Set(
    details.filter((detail) => isPackageHeader(detail.productid)).map((detail) => detail.dsy_cpqfields.dsy_globalid)
  )
  const headerByMember = new Map<string, string>()
  for (const { dsy_cpqfields: fields } of details) {
    // The accessory key wins on an illegal both-keys row, matching the sort.
    const header = fields.dsy_packageglobalid
    if (fields.dsy_globalid && header && headerIds.has(header) && !fields.dsy_parentglobalid) {
      headerByMember.set(fields.dsy_globalid, header)
    }
  }
  const headerByRow = new Map(headerByMember)
  for (const { dsy_cpqfields: fields } of details) {
    const header = fields.dsy_parentglobalid ? headerByMember.get(fields.dsy_parentglobalid) : undefined
    if (fields.dsy_globalid && header) headerByRow.set(fields.dsy_globalid, header)
  }
  return headerByRow
}

/**
 * A change order's rows as a sealed package reads them: nothing inside a package
 * is its own row, and a package whose contents changed shows as its header.
 *
 * A header only diffs on its own fields, so one whose contents alone changed has
 * no row yet — it gets a `changed` row pairing both snapshots' headers, whose
 * price cells roll each side's members in. A package added or removed whole
 * already carries its header's own new/deleted row.
 */
export function collapsePackageChanges(
  changes: DetailChange[],
  currentDetails: QuoteDetailData[],
  previousDetails: QuoteDetailData[]
): DetailChange[] {
  const currentHeaderByRow = getPackageHeaderByGlobalId(currentDetails)
  const previousHeaderByRow = getPackageHeaderByGlobalId(previousDetails)

  // Contents moving in or out change a package without a field diff on any row.
  const touchedHeaders = new Set<string>()
  for (const [globalId, header] of currentHeaderByRow) {
    if (previousHeaderByRow.get(globalId) !== header) touchedHeaders.add(header)
  }
  for (const [globalId, header] of previousHeaderByRow) {
    if (currentHeaderByRow.get(globalId) !== header) touchedHeaders.add(header)
  }

  const visibleChanges = changes.filter((change) => {
    const currentHeader = currentHeaderByRow.get(change.newQuoteDetail?.dsy_cpqfields.dsy_globalid ?? '')
    const previousHeader = previousHeaderByRow.get(change.previousQuoteDetail?.dsy_cpqfields.dsy_globalid ?? '')
    if (currentHeader) touchedHeaders.add(currentHeader)
    if (previousHeader) touchedHeaders.add(previousHeader)
    return !(change.newQuoteDetail ? currentHeader : previousHeader)
  })
  if (!touchedHeaders.size) return visibleChanges

  for (const change of visibleChanges) {
    touchedHeaders.delete((change.newQuoteDetail ?? change.previousQuoteDetail)?.dsy_cpqfields.dsy_globalid ?? '')
  }
  for (const header of touchedHeaders) {
    const current = currentDetails.find((detail) => detail.dsy_cpqfields.dsy_globalid === header)
    const previous = previousDetails.find((detail) => detail.dsy_cpqfields.dsy_globalid === header)
    if (!current || !previous) continue
    visibleChanges.push({
      type: 'changed',
      newQuoteDetail: { ...current, phaseLabel: undefined, systemLabel: undefined, roomLabel: undefined },
      previousQuoteDetail: { ...previous, phaseLabel: undefined, systemLabel: undefined, roomLabel: undefined },
      changes: {},
    })
  }
  return visibleChanges
}

export function sortProposalRoomGroups(
  roomGroups: [string, number[]][],
  sortedChanges: DetailChange[]
): [string, number[]][] {
  const firstChangeIndexByRoom = new Map<string, number>()

  sortedChanges.forEach((change, index) => {
    const fields = (change.newQuoteDetail ?? change.previousQuoteDetail)?.dsy_cpqfields
    const roomId = fields?._dsy_room_value
    if (roomId && !firstChangeIndexByRoom.has(roomId)) {
      firstChangeIndexByRoom.set(roomId, index)
    }
  })

  return roomGroups
    .map(([roomId, systemIds], index) => ({
      group: [roomId, sortProposalSystemIds(systemIds, roomId, sortedChanges)] as [string, number[]],
      index,
    }))
    .sort((a, b) => {
      const aChangeIndex = firstChangeIndexByRoom.get(a.group[0]) ?? Infinity
      const bChangeIndex = firstChangeIndexByRoom.get(b.group[0]) ?? Infinity
      return aChangeIndex - bChangeIndex || a.index - b.index
    })
    .map(({ group }) => group)
}

export function sortProposalSystemIds<T extends number | null>(
  systemIds: T[],
  roomId: string,
  sortedChanges: DetailChange[]
): T[] {
  const firstChangeIndexBySystem = new Map<number | null, number>()

  sortedChanges.forEach((change, index) => {
    const fields = (change.newQuoteDetail ?? change.previousQuoteDetail)?.dsy_cpqfields
    if (fields?._dsy_room_value === roomId && !firstChangeIndexBySystem.has(fields.dsy_system)) {
      firstChangeIndexBySystem.set(fields.dsy_system, index)
    }
  })

  return systemIds
    .map((systemId, index) => ({ systemId, index }))
    .sort(
      (a, b) =>
        (firstChangeIndexBySystem.get(a.systemId) ?? Infinity) -
          (firstChangeIndexBySystem.get(b.systemId) ?? Infinity) ||
        a.index - b.index
    )
    .map(({ systemId }) => systemId)
}
